import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader, ActionButton, GhostButton, FormField, inputClass } from "../components/Primitives";
import { SIDEBAR_LINKS } from "../lib/sidebarConfig";
import { useSidebarLabelsAll } from "../contexts/SidebarLabelsContext";
import { errorMessage } from "../lib/api";
import { Save, RotateCcw, Tag } from "lucide-react";

/**
 * SidebarLabels — admin page for renaming every sidebar tab AND its
 * matching H1 page title. Overrides are per-agency and live under
 * `agencies.sidebar_labels: { key: label }`.
 *
 * Keys:
 *   • Route path (`/clients`) — a leaf sidebar item + its page's H1.
 *   • `section:<group|label>` — a top-level section header (e.g.
 *     "Clients", "Officers", "Operations").
 * Empty string = fall back to the code default.
 */
export default function SidebarLabels() {
  const { overrides, save, loading } = useSidebarLabelsAll();
  const [draft, setDraft] = useState(overrides);
  const [busy, setBusy] = useState(false);

  // Keep draft in sync with context if server-side refresh lands.
  useMemo(() => setDraft(overrides), [overrides]);

  // Flatten the sidebar config into editable rows — section headers on
  // top, then all leaf items grouped under each section.
  const rows = useMemo(() => {
    const out = [];
    for (const item of SIDEBAR_LINKS) {
      if (item.children) {
        const key = `section:${item.group || item.label}`;
        out.push({ key, label: item.label, kind: "section" });
        for (const c of item.children) {
          out.push({ key: c.to, label: c.label, kind: "leaf", parent: item.label });
        }
      } else {
        out.push({ key: item.to, label: item.label, kind: "top" });
      }
    }
    return out;
  }, []);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(overrides), [draft, overrides]);

  const setValue = (key, value) => {
    setDraft((d) => {
      const next = { ...d };
      const trimmed = (value || "").trim();
      if (!trimmed) delete next[key];
      else next[key] = trimmed;
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await save(draft);
      toast.success("Labels saved");
    } catch (err) {
      toast.error(errorMessage(err, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const resetAll = () => {
    if (!confirm("Reset every label to the code default? This clears all customizations in one shot.")) return;
    setDraft({});
  };

  return (
    <div className="space-y-6" data-testid="sidebar-labels-page">
      <PageHeader
        title="Sidebar Labels"
        subtitle="Rename any sidebar tab or section header. Same map also renames the big H1 title on each page. Empty = fall back to the default."
        action={
          <div className="flex items-center gap-2">
            <GhostButton onClick={resetAll} disabled={busy} data-testid="sidebar-labels-reset-all">
              <RotateCcw className="size-3.5" /> Reset all
            </GhostButton>
            <ActionButton onClick={submit} disabled={busy || !dirty || loading} data-testid="sidebar-labels-save">
              <Save className="size-4" /> {busy ? "Saving…" : dirty ? "Save" : "Saved"}
            </ActionButton>
          </div>
        }
      />

      <div className="border border-[#27272A] bg-[#0A0A0A]" data-testid="sidebar-labels-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#27272A] text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
          <div className="col-span-3">Type</div>
          <div className="col-span-3">Default</div>
          <div className="col-span-6">Override</div>
        </div>
        {rows.map((r) => {
          const current = draft[r.key] ?? "";
          const isSection = r.kind === "section";
          return (
            <div
              key={r.key}
              className={`grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-[#1c1c1e] ${isSection ? "bg-[#121212]" : ""}`}
              data-testid={`sidebar-label-row-${r.key}`}
            >
              <div className="col-span-3 flex items-center gap-2 text-xs">
                {isSection ? (
                  <span className="text-[#0A84FF] uppercase tracking-widest font-bold text-[10px]">Section</span>
                ) : r.parent ? (
                  <span className="text-zinc-500 text-[10px]">
                    <Tag className="size-3 inline mr-1" />
                    {r.parent}
                  </span>
                ) : (
                  <span className="text-zinc-500 uppercase tracking-widest text-[10px]">Top-level</span>
                )}
              </div>
              <div className="col-span-3 text-zinc-300 text-sm font-medium">{r.label}</div>
              <div className="col-span-6">
                <FormField label="" hint={r.key}>
                  <input
                    type="text"
                    className={inputClass}
                    placeholder={r.label}
                    value={current}
                    maxLength={40}
                    onChange={(e) => setValue(r.key, e.target.value)}
                    data-testid={`sidebar-label-input-${r.key}`}
                  />
                </FormField>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
